import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    Tab,
    Tabs,
    TextField,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';
import './SearchPopup.scss';

const textFieldSx = {
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
        padding: '0px 14px',
        '& fieldset': {
            borderColor: '#ccc9c0',
        },
        '&:hover fieldset': {
            borderColor: '#aaa',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#ccc9c0',
        },
        '& input': {
            padding: '10px 0px',
        },
    },
};

const tabSx = (selectedTab, index, tabLabels) => ({
    backgroundColor: selectedTab === index ? '#006ad1' : '#F7F9FB',
    color: selectedTab === index ? '#F7F9FB' : '#AEAEAE',
    width: 'auto',
    marginLeft: index !== 0 ? '5px' : '0px',
    marginRight: index !== tabLabels.length - 1 ? '5px' : '0px',
    minWidth: '120px',
    border: selectedTab === index ? '' : '2px solid #989a9c',
    borderRadius: '6px',
    fontSize: '13px',
});

const SearchPopup = ({ searchBarrPopupOpen, handleCloseSearchBarPopup, selectedTab, handleTabChange, tabLabels, searchFormik }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const tabCount = tabLabels.length;

    const goToPrevTab = () => {
        const next = selectedTab <= 0 ? tabCount - 1 : selectedTab - 1;
        handleTabChange(null, next);
    };

    const goToNextTab = () => {
        const next = selectedTab >= tabCount - 1 ? 0 : selectedTab + 1;
        handleTabChange(null, next);
    };

    const renderField = () => {
        if (selectedTab === 0) {
            return (
                <>
                    <TextField
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        name="iv_reference_no"
                        id="iv_reference_no"
                        placeholder="Enter Reference No."
                        value={searchFormik.values.iv_reference_no}
                        onChange={searchFormik.handleChange}
                        onBlur={searchFormik.handleBlur}
                        inputProps={{ maxLength: 14 }}
                        sx={textFieldSx}
                    />
                    {searchFormik.touched.iv_reference_no && searchFormik.errors.iv_reference_no && (
                        <p className="search-popup__error" style={{ color: 'red' }}>{searchFormik.errors.iv_reference_no}</p>
                    )}
                </>
            );
        }
        if (selectedTab === 1) {
            return (
                <>
                    <TextField
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        placeholder="Enter Application ID"
                        name="iv_application_id"
                        id="iv_application_id"
                        value={searchFormik.values.iv_application_id}
                        onChange={searchFormik.handleChange}
                        onBlur={searchFormik.handleBlur}
                        inputProps={{ maxLength: 19, minLength: 17 }}
                        sx={textFieldSx}
                    />
                    {searchFormik.touched.iv_application_id && searchFormik.errors.iv_application_id && (
                        <p className="search-popup__error" style={{ color: 'red' }}>{searchFormik.errors.iv_application_id}</p>
                    )}
                </>
            );
        }
        if (selectedTab === 2) {
            return (
                <>
                    <TextField
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        placeholder="Enter CNIC"
                        name="iv_cnic"
                        id="iv_cnic"
                        value={searchFormik.values.iv_cnic}
                        onChange={searchFormik.handleChange}
                        onBlur={searchFormik.handleBlur}
                        inputProps={{ maxLength: 13 }}
                        sx={textFieldSx}
                    />
                    {searchFormik.touched.iv_cnic && searchFormik.errors.iv_cnic && (
                        <p className="search-popup__error" style={{ color: 'red' }}>{searchFormik.errors.iv_cnic}</p>
                    )}
                </>
            );
        }
        return (
            <>
                <TextField
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    placeholder="Enter Name"
                    name="iv_applicant_name"
                    id="iv_applicant_name"
                    value={searchFormik.values.iv_applicant_name}
                    onChange={searchFormik.handleChange}
                    onBlur={searchFormik.handleBlur}
                    inputProps={{ minLength: 3 }}
                    sx={textFieldSx}
                />
                {searchFormik.touched.iv_applicant_name && searchFormik.errors.iv_applicant_name && (
                    <p className="search-popup__error" style={{ color: 'red' }}>{searchFormik.errors.iv_applicant_name}</p>
                )}
            </>
        );
    };

    return (
        <Dialog
            open={searchBarrPopupOpen}
            onClose={handleCloseSearchBarPopup}
            maxWidth={isMobile ? false : 'sm'}
            fullWidth={isMobile}
            className={`search-popup${isMobile ? ' search-popup--mobile' : ''}`}
            sx={
                isMobile
                    ? {
                        '& .MuiDialog-paper': {
                            borderRadius: '12px',
                        },
                    }
                    : {
                        position: 'fixed',
                        top: '-67%',
                        left: '56%',
                        '& .MuiDialog-paper': {
                            borderRadius: '12px',
                        },
                    }
            }
        >
            <DialogContent
                className="search-popup__content"
                sx={{ background: 'linear-gradient(to bottom, white, #D8E8FF, white)' }}
            >
                {isMobile ? (
                    <div className="search-popup__tab-nav" role="tablist" aria-label="Search type">
                        <IconButton
                            type="button"
                            className="search-popup__tab-nav-btn"
                            onClick={goToPrevTab}
                            aria-label="Previous search type"
                            size="small"
                        >
                            <ChevronLeftIcon fontSize="medium" />
                        </IconButton>
                        <button
                            type="button"
                            className="search-popup__tab-nav-label"
                            onClick={goToNextTab}
                            aria-label={`Current: ${tabLabels[selectedTab]}. Tap for next.`}
                        >
                            {tabLabels[selectedTab]}
                        </button>
                        <IconButton
                            type="button"
                            className="search-popup__tab-nav-btn"
                            onClick={goToNextTab}
                            aria-label="Next search type"
                            size="small"
                        >
                            <ChevronRightIcon fontSize="medium" />
                        </IconButton>
                    </div>
                ) : (
                    <div className="search-popup__tabs-wrap">
                        <Tabs
                            className="search-popup__tabs"
                            value={selectedTab}
                            onChange={handleTabChange}
                            textColor="inherit"
                            variant="standard"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    display: 'none',
                                },
                                color: 'white',
                            }}
                        >
                            {tabLabels.map((label, index) => (
                                <Tab
                                    key={index}
                                    label={label}
                                    sx={tabSx(selectedTab, index, tabLabels)}
                                />
                            ))}
                        </Tabs>
                    </div>
                )}
                <Grid
                    container
                    className="search-popup__form-row"
                    sx={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}
                >
                    <Grid item xs={12} lg={10} className="search-popup__field">
                        {renderField()}
                    </Grid>
                    <Grid item xs={12} lg={1} className="search-popup__submit">
                        <Button
                            fullWidth
                            onClick={searchFormik.handleSubmit}
                            color="primary"
                            variant="contained"
                            className="search-popup__submit-btn"
                            sx={{
                                background: '#006ad1',
                                width: isMobile ? '100%' : '17px',
                                padding: '10px',
                                height: isMobile ? '42px' : '40px',
                            }}
                        >
                            Go
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default SearchPopup;
